"use client";

/**
 * Public activities data provider.
 *
 * Carries the server-fetched Activities section data (activities, per-activity counts, labels, time
 * slots, signup-limit rules) so `ActivitiesRenderer` paints with data on first render instead of
 * firing its own post-hydration fetch. Seeded from the RSC route via `PublicPageProvider`; null in
 * the builder canvas (no provider mounted there — the renderer reads the builder store instead).
 */

import type React from "react";
import { createContext, useContext, useMemo } from "react";

import type { PublicActivitiesInitialData } from "../../_logic/types/public-page-data";
import type { ActivityLabelRow, ActivityRow, ActivityTimeSlotRow, SignupCounts } from "../../_logic/types/db-types";
import type { SignupLimitRules } from "../../_logic/utils/activity-signup-status";
import { groupTimeSlotsByActivity, keySignupCountsByActivity } from "../../_logic/utils/time-slot-utils";

/** Client-ready Activities section seed (arrays keyed / grouped into the lookup shapes the renderer uses). */
export interface PublicActivitiesData {
  activities: ActivityRow[];
  /** activityId -> signup counts. */
  signupCounts: Record<number, SignupCounts>;
  labels: ActivityLabelRow[];
  /** activityId -> its time slots. */
  timeSlotsByActivity: Map<number, ActivityTimeSlotRow[]>;
  signupLimitRules: SignupLimitRules;
}

const PublicActivitiesDataContext = createContext<PublicActivitiesData | null>(null);

/**
 * Read the server-seeded Activities section data.
 *
 * @returns The seed, or null when outside the provider (builder canvas) or the page has no
 *   Activities section.
 */
export function usePublicActivitiesData(): PublicActivitiesData | null {
  return useContext(PublicActivitiesDataContext);
}

interface PublicActivitiesDataProviderProps {
  /** Server-fetched Activities data, or null when the page has no Activities section. */
  initialData: PublicActivitiesInitialData | null;
  children: React.ReactNode;
}

/**
 * Provides the server-seeded Activities data to its subtree.
 *
 * @param props - See PublicActivitiesDataProviderProps.
 * @returns The context provider wrapping `children`.
 */
export function PublicActivitiesDataProvider({
  initialData,
  children,
}: PublicActivitiesDataProviderProps): React.ReactElement {
  // Derived values — key/group the serializable arrays into the lookup shapes the renderer reads.
  const value = useMemo<PublicActivitiesData | null>(() => {
    if (!initialData) return null;

    return {
      activities: initialData.activities,
      signupCounts: keySignupCountsByActivity(initialData.signupCounts),
      labels: initialData.labels,
      timeSlotsByActivity: groupTimeSlotsByActivity(initialData.timeSlots),
      signupLimitRules: {
        dayCapsByDate: new Map(initialData.dayCaps),
        exclusionGroups: initialData.exclusionGroups,
      },
    };
  }, [initialData]);

  // Render
  return <PublicActivitiesDataContext.Provider value={value}>{children}</PublicActivitiesDataContext.Provider>;
}
